/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable indent */
import './style.scss';

import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { getCompletedProjects } from 'apis/project.api';

import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { catchError, isAuthorized, mapError } from 'utils/func';
import { Tag } from 'primereact/tag';
import { TIMEZONE_VN } from 'constant';
import { Menu } from 'primereact/menu';
import { Button } from 'primereact/button';
import { useDownloadCertificationDocxFile } from 'utils/hooks/projects';

function ProjectCompletedViewList() {
  // #region Data
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useQuery('project', () => getCompletedProjects());
  const projects = useMemo(() => data?.data?.data ?? [], [data?.data]);
  const statuses = t('project.viewList.statuses', { returnObjects: true });

  // #endregion

  // #region Format
  const formatVietnameseName = (value) => (
    <Link
      to={`/project/${value?.id}?projectProposalId=${value?.projectProposal?.id}`}
      className="hover:underline "
    >
      {value?.projectProposal?.vietnameseName}
    </Link>
  );
  const formatOwner = (value) => value?.projectProposal?.owner?.fullname;
  const formatOrganization = (value) =>
    t('lang') === 'vi'
      ? value?.projectProposal?.projectProposalOrganizations[0]?.name
      : value?.projectProposal?.projectProposalOrganizations[0]?.englishName;
  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString('en-GB', { timeZone: TIMEZONE_VN }) : '';

  const formatStartDate = (value) => formatDate(value?.startDate);

  const formatEndDate = (value) => formatDate(value?.endDate);
  const formatTotalExpenditure = (value) => {
    if (value?.totalExpenditure === 0 && value?.proposedExpenditureSummaries?.length > 0) {
      const total = value?.proposedExpenditureSummaries.reduce((sum, cur) => {
        if (cur?.proposedExpenditureType?.subProposedExpenditureTypeId === null) {
          const expenditureFromFunding = cur?.expenditureFromFunding ?? 0;
          const expenditureFromVNUHCM = cur?.expenditureFromVNUHCM ?? 0;

          return sum + expenditureFromFunding + expenditureFromVNUHCM;
        }
        return sum;
      }, 0);
      return total?.toLocaleString('vi');
    }
    return value?.totalExpenditure?.toLocaleString('vi') || 0;
  };
  // #region Render
  const formatStatus = (value) => {
    const statusColorMapping = {
      new: 'info',
      doing: 'success',
      'mid-term': 'success',
      final: 'success',
      acceptance: 'success',
      submit: 'submit',
      liquidation: 'success',
      completed: 'success',
    };

    return (
      <Tag
        style={{ minWidth: '4rem' }}
        severity={statusColorMapping[value.projectStatus.name]}
        value={statuses[value.projectStatus.name]}
        rounded
      />
    );
  };

  const user = JSON.parse(localStorage.getItem('user'));

  const { mutateAsync } = useDownloadCertificationDocxFile();

  const handleDownloadCertification = async (projectId) => {
    try {
      const response = await mutateAsync(projectId);
      const url = window.URL.createObjectURL(
        new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        })
      );
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certification_project_${projectId}.docx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (err) {
      catchError(t, err);
    }
  };

  const formatActions = (value) => {
    const menuRef = useRef(null);

    const items = [];

    // Project adjustment
    if (
      value?.projectProposal?.owner?.id === user?.id ||
      isAuthorized(user?.id, value?.projectProposal?.projectProposalContacts)
    ) {
      items.push({
        template: () => (
          <div onClick={() => handleDownloadCertification(value?.id)} className="p-menuitem-link">
            <span className="p-menuitem-text">
              {t('projectCompleted.action.downloadCertification')}
            </span>
          </div>
        ),
      });
    }

    return (
      <div className="flex justify-content-center">
        <Menu model={items} popup ref={menuRef} style={{ minWidth: '250px' }} />
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <Button
            icon="pi pi-ellipsis-v"
            className="p-button-rounded p-button-text"
            onClick={(event) => menuRef.current.toggle(event)}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="grid">
      {/* {true && <Loading />} */}
      <div className="col-12">
        <div className="card">
          {isError && <h3 className="text-red-600">{mapError(t, error)}</h3>}
          <div className="flex justify-content-between align-items-end mb-4 pb-2">
            <h5 className="p-0 m-0">{t('project.viewList.title')}</h5>
          </div>

          <DataTable
            value={projects}
            className="p-datatable-gridlines header-table"
            showGridlines
            dataKey="id"
            filterDisplay="menu"
            header=""
            scrollable
            loading={isLoading}
            // responsiveLayout="scroll"
            emptyMessage={t('projectProposal.viewList.emptyMessage')}
          >
            <Column
              field="projectProposal.vietnameseName"
              body={formatVietnameseName}
              header={t('project.viewList.vietnameseName')}
              style={{
                minWidth: '10rem',
                maxWidth: '16rem',
                textAlign: 'center',
              }}
              frozen
              alignFrozen="left"
            />
            <Column
              header={t('project.viewList.owner')}
              style={{ minWidth: '8rem', textAlign: 'center' }}
              body={formatOwner}
            />
            <Column
              header={t('project.viewList.loadOrganization')}
              style={{ minWidth: '8rem', textAlign: 'center' }}
              body={formatOrganization}
            />
            <Column
              header={t('project.viewList.totalExpenditure')}
              style={{ minWidth: '8rem', textAlign: 'center' }}
              body={formatTotalExpenditure}
            />
            <Column
              header={t('project.viewList.startDate')}
              style={{ minWidth: '8rem', textAlign: 'center' }}
              body={formatStartDate}
            />
            <Column
              header={t('project.viewList.endDate')}
              style={{ minWidth: '8rem' }}
              body={formatEndDate}
            />
            <Column
              field="projectStatus.name"
              header={t('project.viewList.status')}
              style={{ minWidth: '8rem', textAlign: 'center' }}
              body={formatStatus}
            />
            <Column
              header={t('project.action')}
              body={formatActions}
              style={{ minWidth: '6rem', maxWidth: '16rem', textAlign: 'center' }}
              frozen
              alignFrozen="right"
            />
          </DataTable>
        </div>
      </div>
    </div>
  );
}

export default ProjectCompletedViewList;
